import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Settings2, Store } from 'lucide-react';
import { listCommerceRules } from '../../../../api/collections';
import { getActiveClientsApi } from '../../../../api/company';
import useAuth from '../../../../hooks/useAuth';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { EmptyState } from '../../../../components/ui/EmptyState';
import { SearchableSelect } from '../../../../components/ui/SearchableSelect';
import { TableSkeleton } from '../../../../components/ui/TableSkeleton';
import CommerceCollectionRuleDrawer from '../components/CommerceCollectionRuleDrawer';
import { formatCommerceOptionLabel } from '../components/commerceOptionLabel';
import { matchCommerce, toCommerceOption } from '../../../../utils/commerceSearch';
import { collectionsErrorText, formatInstant } from '../utils/collectionReminders';

const PAGE_SIZE = 50;
// Comercio · Plazo · Automáticos · Contactos de cobranza · Actualizada
const GRID_COLS = '1.6fr 0.8fr 0.9fr 1.6fr 1fr';

// react-select entrega `{ label, value, data }`: los campos del comercio quedan en `data`.
const filterCommerceOption = (option, query) => matchCommerce(option.data, query);

/**
 * Excepciones por comercio: los comercios con plazo propio, recordatorios automáticos
 * pausados o contactos de cobranza propios (el resto hereda todo del operador). Cada fila
 * abre la regla en un panel lateral; un buscador permite configurar un comercio que todavía
 * no tiene excepciones.
 *
 * @param {{ timeZone?: string }} props
 */
export default function CommerceRulesTab({ timeZone }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const logisticCompanyId = user?.logisticCompany?._id ?? null;

  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commerceOptions, setCommerceOptions] = useState([]);
  const [pickedCompanyId, setPickedCompanyId] = useState('');
  const [drawer, setDrawer] = useState(null); // { companyId, companyName }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await listCommerceRules({ limit: PAGE_SIZE, offset });
      setPage({ items: data?.items ?? [], count: data?.count ?? 0 });
    } catch (err) {
      setError(collectionsErrorText(err, t, 'No se pudieron cargar las excepciones por comercio.'));
    } finally {
      setLoading(false);
    }
  }, [offset, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Comercios con contrato activo, para configurar uno que todavía no tiene excepción.
  // getActiveClientsApi no lanza: ante un fallo devuelve { error } y el buscador queda vacío.
  useEffect(() => {
    if (!logisticCompanyId) return undefined;
    let active = true;
    getActiveClientsApi(logisticCompanyId).then((data) => {
      if (!active || !Array.isArray(data)) return;
      setCommerceOptions(data.map((c) => toCommerceOption(c, t('Sin nombre'))));
    });
    return () => {
      active = false;
    };
  }, [logisticCompanyId, t]);

  const pickedOption = useMemo(
    () => commerceOptions.find((o) => o.value === pickedCompanyId) ?? null,
    [commerceOptions, pickedCompanyId],
  );

  const totalPages = Math.max(1, Math.ceil(page.count / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const open = (companyId, companyName) => setDrawer({ companyId, companyName });
  const termText = (r) =>
    Number(r.paymentTermDays) > 0 ? t('{{n}} días', { n: r.paymentTermDays }) : t('Hereda');
  const pausedBadge = (r) =>
    r.remindersPaused ? <Badge tone="warning">{t('Pausados')}</Badge> : <Badge tone="neutral">{t('Activos')}</Badge>;
  const contactsText = (r) => {
    const parts = [];
    if (r.contactEmails?.length) parts.push(t('{{n}} correos', { n: r.contactEmails.length }));
    if (r.contactPhone) parts.push(r.contactPhone);
    return parts.length ? parts.join(' · ') : t('Los del comercio');
  };

  return (
    <div className="space-y-3">
      <div className="bill-card" style={{ overflow: 'visible' }}>
        <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-end">
          <div className="bill-field flex-1">
            <span className="bill-label">{t('Configurar otro comercio')}</span>
            <SearchableSelect
              options={commerceOptions}
              value={pickedCompanyId}
              onChange={(v) => setPickedCompanyId(v || '')}
              placeholder={t('Buscar por nombre, razón social o RUT…')}
              isClearable
              noOptionsText={t('Sin comercios')}
              filterOption={filterCommerceOption}
              formatOptionLabel={formatCommerceOptionLabel}
            />
          </div>
          <Button
            variant="outline"
            iconLeft={<Settings2 className="h-4 w-4" />}
            disabled={!pickedOption}
            onClick={() => open(pickedOption.value, pickedOption.name ?? pickedOption.label)}
          >
            {t('Configurar')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={load}>
            {t('Reintentar')}
          </Button>
        </div>
      )}

      <div className="bill-card bill-card--clip">
        {loading ? (
          <TableSkeleton rows={5} columns={5} className="border-0" />
        ) : page.items.length === 0 ? (
          !error && (
            <EmptyState
              icon={<Store />}
              title={t('Ningún comercio tiene excepciones')}
              description={t('Todos heredan el plazo y la regla del operador. Se configuran desde Empresas → Cobranza, desde "Mayores morosos" del resumen o con el buscador de arriba.')}
            />
          )
        ) : isMobile ? (
          <div className="bill-cards">
            {page.items.map((r) => (
              <div
                key={r.companyId}
                className="bill-card-item bill-card-item--clickable"
                role="button"
                tabIndex={0}
                onClick={() => open(r.companyId, r.companyName)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') open(r.companyId, r.companyName);
                }}
              >
                <div className="bill-card-item__head">
                  <span className="bill-card-item__title">{r.companyName || t('Sin nombre')}</span>
                  {pausedBadge(r)}
                </div>
                <div className="bill-card-item__rows">
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Plazo')}</span>
                    <span className="bill-card-item__v">{termText(r)}</span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Contactos de cobranza')}</span>
                    <span className="bill-card-item__v">{contactsText(r)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': GRID_COLS }}>
            <div className="bill-grid__head" role="row">
              <span className="bill-grid__hcell" role="columnheader">{t('Comercio')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Plazo')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Automáticos')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Contactos de cobranza')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Actualizada')}</span>
            </div>
            {page.items.map((r) => (
              <div
                key={r.companyId}
                className="bill-grid__row bill-grid__row--clickable"
                role="row"
                tabIndex={0}
                title={t('Editar la regla de {{name}}', { name: r.companyName || t('Sin nombre') })}
                onClick={() => open(r.companyId, r.companyName)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') open(r.companyId, r.companyName);
                }}
              >
                <span className="bill-td-strong" role="cell">
                  {r.companyName || <span className="bill-td-sub">{t('Sin nombre')}</span>}
                </span>
                <span className="bill-td-muted" role="cell">{termText(r)}</span>
                <span role="cell">{pausedBadge(r)}</span>
                <span className="bill-td-muted" role="cell">{contactsText(r)}</span>
                <span className="bill-td-sub" role="cell">{formatInstant(r.updatedAt, timeZone) || '—'}</span>
              </div>
            ))}
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="bill-pagination">
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label={t('Anterior')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>
              {t('Página')} {currentPage} {t('de')} {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage >= totalPages}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label={t('Siguiente')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <CommerceCollectionRuleDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        companyId={drawer?.companyId ?? null}
        companyName={drawer?.companyName ?? null}
        onSaved={load}
      />
    </div>
  );
}
